import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, useMatch } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from './hooks/useTranslation';
import { useAuth } from './contexts/AuthContext';
import { apiGet, setClanSlug } from './utils/api';
import Sidebar from './components/Sidebar';
import MobileNav from './components/MobileNav';
import Dashboard from './components/Dashboard';
import ClanInfo from './components/ClanInfo';
import Rules from './components/Rules';
import Scoring from './components/Scoring';
import HallOfFame from './components/HallOfFame';
import EventsFeed from './components/EventsFeed';
import OnboardingModal from './components/OnboardingModal';
import SettingsPage from './components/SettingsPage';
import ManageClansPage from './components/ManageClansPage';
import PageHeader from './components/PageHeader';
import Breadcrumbs from './components/Breadcrumbs';
import LoadingSpinner from './components/LoadingSpinner';
import PlayerProfile from './components/PlayerProfile';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Imprint from './components/Imprint';

// Main app shell – rendered under /clan/:slug
function ClanApp() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const playerMatch = useMatch('/clan/:slug/player/:playerTag');
  const { t, lang, setLang, ready: i18nReady } = useTranslation();
  const { isAuthenticated, userProfile, isBanned, loading: authLoading, logout, profileLoaded } = useAuth();
  const [activePage, setActivePage] = useState(() => localStorage.getItem('activePage') || 'dashboard');
  const [currentSeason, setCurrentSeason] = useState(() => {
    const saved = localStorage.getItem(`currentSeason_${slug}`);
    return saved || '';
  });
  const userSelectedSeason = useRef(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [pendingSeason, setPendingSeason] = useState(null); // Track active season change for loading state
  const registrationSubmittedRef = useRef(false);
  const modalBackdropRef = useRef(null);

  // Set the clan slug for the API module and load saved season for this clan
  useEffect(() => {
    setClanSlug(slug);
    window.scrollTo(0, 0);
    // Load saved season for this clan
    const savedSeason = localStorage.getItem(`currentSeason_${slug}`);
    const savedUserSelected = localStorage.getItem(`userSelectedSeason_${slug}`);
    setCurrentSeason(savedSeason || '');
    userSelectedSeason.current = savedUserSelected === 'true';
    // Reset pending season when switching clans
    setPendingSeason(null);
  }, [slug]);

  // --- React Query: clans list ---
  const { data: clansData } = useQuery({
    queryKey: ['clans_list'],
    queryFn: () => apiGet('/v1/clans'),
    staleTime: 300_000,
  });
  const clans = Array.isArray(clansData) ? clansData : [];
  
  // Get discord_invite_link for current clan
  const currentClan = clans.find(c => c.slug === slug);
  const discordInviteLink = currentClan?.discord_invite_link || '';

  // --- React Query: clan header ---
  const { data: clanHeaderData, error: clanHeaderError, isError: isClanHeaderError } = useQuery({
    queryKey: ['clan_header', slug],
    queryFn: () => apiGet(`/v1/clan/${slug}/header`),
    staleTime: 300_000,
    refetchInterval: 300_000,
    placeholderData: () => {
      try { return JSON.parse(localStorage.getItem('clanHeader_' + slug)) || {}; } catch { return {}; }
    },
  });

  // Show Not Found page if clan doesn't exist (404 from API)
  if (isClanHeaderError && clanHeaderError?.message?.includes('404')) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🏰</div>
          <h1 className="text-2xl font-bold text-gray-200 mb-2">{t('not_found_title') || 'Clan Not Found'}</h1>
          <p className="text-gray-400 mb-6">{t('clan_not_found_desc', { slug }) || `The clan "${slug}" does not exist.`}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
          >
            {t('back_to_home') || 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  // Persist clan header to localStorage when it changes
  useEffect(() => {
    if (clanHeaderData && clanHeaderData.name) {
      try { localStorage.setItem('clanHeader_' + slug, JSON.stringify(clanHeaderData)); } catch {}
    }
  }, [clanHeaderData, slug]);

  const clanHeader = clanHeaderData && clanHeaderData.name ? clanHeaderData : (
    (() => { try { return JSON.parse(localStorage.getItem('clanHeader_' + slug)) || {}; } catch { return {}; } })()
  );

  // Check if user needs onboarding (authenticated but no profile or rejected)
  // Suppressed if a registration was just submitted or is pending in sessionStorage
  useEffect(() => {
    if (isAuthenticated && !authLoading && profileLoaded && userProfile && (userProfile.status === 'none' || userProfile.status === 'rejected')) {
      if (registrationSubmittedRef.current) return;
      if (sessionStorage.getItem('pending_registration')) return;
      setShowOnboarding(true);
    }
  }, [isAuthenticated, authLoading, profileLoaded, userProfile]);

  // --- React Query: seasons ---
  const { data: seasonsData } = useQuery({
    queryKey: ['seasons', slug],
    queryFn: () => apiGet(`/v1/clan/${slug}/seasons`),
    staleTime: 300_000,
    refetchInterval: 300_000,
  });

  const seasons = seasonsData?.seasons || [];
  const serverCurrentSeason = seasonsData?.current_season || seasons[0] || '';

  // Auto-select current season from server when it arrives
  useEffect(() => {
    if (!serverCurrentSeason) return;
    setCurrentSeason(prev => {
      if (!prev) {
        // Save auto-selected season to localStorage
        localStorage.setItem(`currentSeason_${slug}`, serverCurrentSeason);
        localStorage.setItem(`userSelectedSeason_${slug}`, 'false');
        return serverCurrentSeason;
      }
      // Always auto-switch to the new season when server current season changes
      // This ensures users see the new season immediately after season transition
      if (prev !== serverCurrentSeason) {
        // Save auto-selected season to localStorage
        localStorage.setItem(`currentSeason_${slug}`, serverCurrentSeason);
        localStorage.setItem(`userSelectedSeason_${slug}`, 'false');
        userSelectedSeason.current = false;
        return serverCurrentSeason;
      }
      return prev;
    });
  }, [serverCurrentSeason, slug]);

  // --- React Query: data for selected season ---
  const { data: seasonData, isLoading: isSeasonDataLoading, isFetching: isSeasonDataFetching } = useQuery({
    queryKey: ['data', slug, currentSeason],
    queryFn: () => apiGet(`/v1/clan/${slug}/data?season=${currentSeason}`),
    enabled: !!currentSeason,
    refetchInterval: 60_000, // Refetch every 60s - war data changes frequently
    placeholderData: keepPreviousData,
  });

  const leaderboardData = seasonData?.leaderboard || [];
  const warInfo = seasonData?.war || null;
  const warHistory = seasonData?.war_history || [];
  const lastSyncAt = seasonData?.last_sync_at || null;

  // Clear pending season once data fetch completes
  useEffect(() => {
    if (!isSeasonDataFetching && pendingSeason) {
      setPendingSeason(null);
    }
  }, [isSeasonDataFetching, pendingSeason]);

  // Show dashboard-level spinner only on initial load (no season data at all)
  const isDashboardLoading = !currentSeason || !seasonsData || isSeasonDataLoading;
  // Show leaderboard spinner only when user actively changed season and data is still loading
  const isLeaderboardFetchingNewSeason = pendingSeason && isSeasonDataFetching;

  const handlePageChange = useCallback((page) => {
    setActivePage(page);
    localStorage.setItem('activePage', page);
  }, []);

  const handleSeasonChange = useCallback((season) => {
    userSelectedSeason.current = true;
    setPendingSeason(season); // Mark as pending while loading
    setCurrentSeason(season);
    // Save to localStorage for persistence
    localStorage.setItem(`currentSeason_${slug}`, season);
    localStorage.setItem(`userSelectedSeason_${slug}`, 'true');
  }, [slug]);

  const formatSeasonLabel = useCallback((season) => {
    if (!season) return '';
    const [year, month] = season.split('-');
    const monthName = t('month_' + parseInt(month, 10));
    return `${monthName} ${year}`;
  }, [t]);

  const handleOnboardingComplete = useCallback(() => {
    registrationSubmittedRef.current = true;
    setShowOnboarding(false);
  }, []);

  const pages = {
    dashboard: isDashboardLoading ? (
      <div id="dashboardPage">
        <h1 className="text-2xl md:text-3xl mb-6">🔥 <span>{t('page_dashboard')}</span></h1>
        <LoadingSpinner />
      </div>
    ) : (
      <Dashboard
        t={t}
        lang={lang}
        leaderboardData={leaderboardData}
        isLeaderboardLoading={isLeaderboardFetchingNewSeason}
        warInfo={warInfo}
        warHistory={warHistory}
        lastSyncAt={lastSyncAt}
        seasons={seasons}
        currentSeason={currentSeason}
        serverCurrentSeason={serverCurrentSeason}
        onSeasonChange={handleSeasonChange}
        formatSeasonLabel={formatSeasonLabel}
        slug={slug}
      />
    ),
    clan: <ClanInfo t={t} slug={slug} />,
    rules: <Rules t={t} slug={slug} />,
    info: <Scoring t={t} slug={slug} />,
    halloffame: <HallOfFame t={t} slug={slug} formatSeasonLabel={formatSeasonLabel} />,
    events: <EventsFeed t={t} slug={slug} lastSyncAt={lastSyncAt} />,
  };

  // Wait for translations on very first visit (no localStorage cache yet).
  // On subsequent visits this is true immediately from the cached data.
  if (!i18nReady) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <LoadingSpinner />
      </div>
    );
  }

  // Lock body scroll when player modal is open
  useEffect(() => {
    if (playerMatch) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [playerMatch]);

  const handleClosePlayerModal = () => {
    navigate(`/clan/${slug}`);
  };

  return (
    <>
      {showOnboarding && (
        <OnboardingModal
          clans={clans}
          onComplete={handleOnboardingComplete}
          onClose={() => setShowOnboarding(false)}
        />
      )}

      <MobileNav
        t={t}
        lang={lang}
        setLang={setLang}
        activePage={activePage}
        onPageChange={handlePageChange}
        clanHeader={clanHeader}
        discordInviteLink={discordInviteLink}
      />
      <div className="flex flex-1 md:flex-row gap-0">
        <Sidebar
          t={t}
          lang={lang}
          setLang={setLang}
          activePage={activePage}
          onPageChange={handlePageChange}
          clanHeader={clanHeader}
          discordInviteLink={discordInviteLink}
          userProfile={userProfile}
        />
        <div id="content" className="flex-1 p-4 pt-16 md:pt-6 md:pr-6 md:pl-4">
          {pages[activePage] || pages.dashboard}
        </div>
      </div>

      {/* Player Profile Modal Overlay */}
      {playerMatch && (
        <div className="fixed inset-0 z-50" style={{ pointerEvents: 'auto' }}>
          {/* Backdrop - handles click outside */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleClosePlayerModal}
          />
          {/* Scrollable container */}
          <div className="absolute inset-0 overflow-y-auto">
            <div className="min-h-full px-4 py-8 flex items-start justify-center">
              {/* Modal content - stops click propagation */}
              <div
                className="max-w-6xl w-full bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 relative"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={handleClosePlayerModal}
                  className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors border border-gray-600"
                  aria-label="Close"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
                <PlayerProfile
                  slug={slug}
                  playerTag={playerMatch.params.playerTag}
                  t={t}
                  isModal
                  onClose={handleClosePlayerModal}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const SETTINGS_LANGS = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

function SettingsLangSwitcher({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="lp-lang-switcher" ref={ref}>
      <button onClick={() => setOpen(o => !o)} className="lp-lang-btn">
        <span className="lp-lang-label">{SETTINGS_LANGS.find(l => l.code === lang)?.label || lang.toUpperCase()}</span>
        <span className="lp-lang-caret">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="lp-lang-menu">
          {SETTINGS_LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`lp-lang-option${l.code === lang ? ' active' : ''}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsRoute() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { loading: authLoading, userProfile, isAdmin, isOwner, profileLoaded } = useAuth();
  const { t, lang, setLang } = useTranslation();

  useEffect(() => {
    setClanSlug(slug);
  }, [slug]);

  if (authLoading || !profileLoaded) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <LoadingSpinner />
      </div>
    );
  }

  // Owners can access any clan's settings
  // Admins can only access their own clan's settings
  const isAdminOfThisClan = userProfile?.memberships?.some(
    m => m.slug === slug && m.role === 'admin'
  );
  const canAccessSettings = isOwner || isAdminOfThisClan;

  // If no access, show "Not Found" page (same UX as invalid URL)
  if (!canAccessSettings) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100">
        <PageHeader lang={lang} setLang={setLang} t={t} currentSlug={slug} />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold text-gray-200 mb-2">{t('not_found_title') || 'Page Not Found'}</h1>
            <p className="text-gray-400 mb-6">{t('settings_no_access') || 'You do not have access to these settings.'}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
            >
              {t('back_to_home') || 'Back to Home'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: t('breadcrumb_home') || 'Home', path: '/' },
    { label: t('breadcrumb_dashboard') || 'Dashboard', path: `/clan/${slug}` },
    { label: t('breadcrumb_settings') || 'Settings', path: null },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <PageHeader lang={lang} setLang={setLang} t={t} currentSlug={slug} />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        <SettingsPage slug={slug} t={t} lang={lang} setLang={setLang} />
      </div>
    </div>
  );
}

export default function App() {
  const { isBanned, logout } = useAuth();

  if (isBanned) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="max-w-md w-full mx-4 bg-gray-800 border border-red-700/50 rounded-2xl p-8 text-center shadow-xl">
          <div className="text-red-500 text-5xl mb-4">🚫</div>
          <h1 className="text-xl font-bold text-red-400 mb-2">Account Banned</h1>
          <p className="text-gray-400 text-sm mb-6">
            Your account has been banned from this service. If you believe this is a mistake, please contact the website owner.
          </p>
          <button
            onClick={logout}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPolicyRoute />} />
      <Route path="/terms" element={<TermsOfServiceRoute />} />
      <Route path="/imprint" element={<ImprintRoute />} />
      <Route path="/clan/:slug/*" element={<ClanApp />} />
      <Route path="/settings/:slug" element={<SettingsRoute />} />
      <Route path="/manage" element={<ManageClansPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PrivacyPolicyRoute() {
  const { t, lang, setLang } = useTranslation();
  return <PrivacyPolicy t={t} lang={lang} setLang={setLang} />;
}

function TermsOfServiceRoute() {
  const { t, lang, setLang } = useTranslation();
  return <TermsOfService t={t} lang={lang} setLang={setLang} />;
}

function ImprintRoute() {
  const { t, lang, setLang } = useTranslation();
  return <Imprint t={t} lang={lang} setLang={setLang} />;
}
